const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_YEAR_MS = 365 * ONE_DAY_MS;

// French abbreviations matching the Figma design (AS-2259). Intl's "short"
// month for fr yields "janv.", "févr.", etc., so we map explicitly to the
// 3-letter style requested in the ticket.
const FR_SHORT_MONTHS = [
  "jan",
  "fév",
  "mar",
  "avr",
  "mai",
  "juin",
  "juil",
  "aoû",
  "sep",
  "oct",
  "nov",
  "déc"
];

const isFrench = (locale: string): boolean =>
  locale.toLowerCase().startsWith("fr");

const formatTimeOnly = (date: Date, locale: string): string => {
  if (isFrench(locale)) {
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  }

  // en: build "11:59pm" (lowercase, no separator). formatToParts avoids the
  // narrow no-break space (U+202F) Intl inserts before the day period.
  const parts = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  }).formatToParts(date);

  const hour = parts.find((p) => p.type === "hour")?.value ?? "";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value ?? "";

  return `${hour}:${minute}${dayPeriod.toLowerCase()}`;
};

const formatDateOnly = (
  date: Date,
  locale: string,
  withYear: boolean
): string => {
  if (isFrench(locale)) {
    const month = FR_SHORT_MONTHS[date.getMonth()];
    const base = `${date.getDate()} ${month}`;
    return withYear ? `${base} ${date.getFullYear()}` : base;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: withYear ? "numeric" : undefined
  }).format(date);
};

export const formatLastActivityDate = (
  dateString: string,
  locale = "en"
): string => {
  const date = new Date(dateString);
  const diffInMs = Date.now() - date.getTime();

  if (diffInMs < ONE_DAY_MS) {
    return formatTimeOnly(date, locale);
  }

  return formatDateOnly(date, locale, diffInMs >= ONE_YEAR_MS);
};

export const formatMessageTimeLabel = (
  dateString: string,
  locale = "en"
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit"
  });

  const relativeTimeFormat = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto"
  });

  const diffTime = nowDate.getTime() - messageDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const todayLabel = relativeTimeFormat.format(0, "day");
    return `${todayLabel} ${timeFormatter.format(date)}`;
  } else if (diffDays === 1) {
    const yesterdayLabel = relativeTimeFormat.format(-1, "day");
    return `${yesterdayLabel} ${timeFormatter.format(date)}`;
  } else {
    // Other dates
    const dateFormatter = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year:
        messageDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      hour: "2-digit",
      minute: "2-digit"
    });
    return dateFormatter.format(date);
  }
};

export const formatMessageTime = (
  dateString: string,
  locale = "en"
): string => {
  const date = new Date(dateString);
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit"
  });
  return timeFormatter.format(date);
};

export const formatMessageDateSeparator = (
  dateString: string,
  locale = "en"
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const relativeTimeFormat = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto"
  });

  const diffTime = nowDate.getTime() - messageDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  let result: string;

  if (diffDays === 0) {
    result = relativeTimeFormat.format(0, "day");
  } else if (diffDays === 1) {
    result = relativeTimeFormat.format(-1, "day");
  } else {
    const dateFormatter = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year:
        messageDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined
    });
    result = dateFormatter.format(date);
  }

  return result.charAt(0).toUpperCase() + result.slice(1);
};
